import { KEYBOARD_EVENTS } from '@/components/SearchAssistant/constants';
import { shouldPreventSubmit } from '@/components/SearchAssistant/helpers/inputHelpers';
import { useOptimisticMessageCreation } from '@/components/SearchAssistant/hooks/useChatData';
import { searchAssistantStore } from '@/stores/searchAssistantStore';
import { Send } from '@mui/icons-material';
import { Box, CircularProgress, IconButton, TextField } from '@mui/material';
import { memo, useCallback } from 'react';

const styles = {
  inputContainer: {
    position: 'relative',
    padding: (theme) => theme.spacing(1),
    borderTop: (theme) => `1px solid ${theme.palette.baseColors.deepSeaNavy.c25}`,
    backgroundColor: (theme) => theme.palette.baseColors.offWhite.c50,
  },
  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  textarea: {
    position: 'relative',
    width: '100%',
    zIndex: 10,
    '& textarea': {
      paddingRight: (theme) => theme.spacing(5),
    },
    '& .Mui-focused': {
      '> fieldset.MuiOutlinedInput-notchedOutline': {
        border: (theme) => `1px solid ${theme.palette.baseColors.deepSeaNavy.c100}50`,
      },
    },
  },
  sendButton: {
    position: 'absolute',
    zIndex: 15,
    right: (theme) => theme.spacing(1),
    top: '50%',
    transform: 'translateY(-50%)',
    padding: (theme) => theme.spacing(0.75),
    color: (theme) => theme.palette.baseColors?.fireOrange?.c100,
    '&:hover': {
      color: (theme) => `${theme.palette.baseColors?.fireOrange?.c100}/50`,
    },
    '&:disabled': {
      color: (theme) => theme.palette.baseColors.deepSeaNavy.c25,
    },
  },
};

const ChatInputTextArea = memo(({ handleSubmit }) => {
  const input = searchAssistantStore.use.input();
  const setInput = searchAssistantStore.use.setInput();
  const contentIsLoading = searchAssistantStore.use.contentIsLoading();
  const handleInputChange = useCallback((e) => setInput(e.target.value), [setInput]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === KEYBOARD_EVENTS.ENTER && !e.shiftKey) {
        handleSubmit(e);
      }
    },
    [handleSubmit],
  );

  return (
    <>
      <TextField
        autoFocus
        value={input}
        onChange={handleInputChange}
        label="Type your message"
        sx={styles.textarea}
        multiline
        onKeyDown={handleKeyDown}
        disabled={contentIsLoading}
      />
      <IconButton
        type="submit"
        disabled={shouldPreventSubmit(input, contentIsLoading)}
        sx={styles.sendButton}
        size="small"
      >
        {contentIsLoading ? <CircularProgress size={24} /> : <Send fontSize="small" />}
      </IconButton>
    </>
  );
});

const ChatInput = ({ onSubmit }) => {
  return (
    <Box sx={styles.inputContainer}>
      <form onSubmit={onSubmit}>
        <Box sx={styles.inputWrapper}>
          <ChatInputTextArea handleSubmit={onSubmit} />
        </Box>
      </form>
    </Box>
  );
};

export const ChatInputWrapper = () => {
  const createMessage = useOptimisticMessageCreation();
  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const currentInput = searchAssistantStore.getState().input;
      const sessionId = searchAssistantStore.getState().currentSessionId;
      createMessage(sessionId, currentInput);
    },
    [createMessage],
  );

  return <ChatInput onSubmit={handleSubmit} />;
};
